import { jsPDF } from 'jspdf'
// Vite resolves these to the built asset URLs
import modelmaticBannerLogoUrl from '@/assets/images/modelmatic_banner_logo_text.png'
import modelmaticLogoUrl from '@/assets/images/modelmatic_logo.png'
import type { TranscriptResponse } from '@/client/types.gen'
import { wrapTextForPdf } from '@/utils/pdfText'

export interface TranscriptExportMetadata {
	artifactName?: string
	projectName?: string
	createdByDisplay?: string
	companyName?: string
}

const MARGIN = 20
const PAGE_WIDTH = 210 // A4 mm
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN
const LOGO_MAX_WIDTH = 45
const BANNER_MAX_WIDTH = 70
const LOGO_MAX_HEIGHT = 14
const HEADER_HEIGHT = 25
const TITLE_FONT_SIZE = 18
const TITLE_LINE_HEIGHT = 6
const BODY_FONT_SIZE = 10
const SPEAKER_LINE_FONT_SIZE = 9
const SUBTITLE_FONT_SIZE = 9
const SECTION_SPACING = 8
const UTTERANCE_BLOCK_GAP_MM = 3
const HEADER_FONT_SIZE = 9
const FOOTER_FONT_SIZE = 8

interface LogoData {
	dataUrl: string
	width: number
	height: number
}

async function loadBannerWithDimensions(): Promise<LogoData | null> {
	try {
		const response = await fetch(modelmaticBannerLogoUrl)
		if (!response.ok) return null
		const blob = await response.blob()
		const dataUrl = await new Promise<string | null>((resolve) => {
			const reader = new FileReader()
			reader.onloadend = () => resolve(reader.result as string)
			reader.onerror = () => resolve(null)
			reader.readAsDataURL(blob)
		})
		if (!dataUrl) return null

		const { width, height } = await new Promise<{ width: number; height: number }>(
			(resolve, reject) => {
				const img = new Image()
				img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
				img.onerror = () => reject(new Error('Failed to load image'))
				img.src = dataUrl
			}
		)
		return { dataUrl, width, height }
	} catch {
		return null
	}
}

async function loadLogoWithDimensions(): Promise<LogoData | null> {
	try {
		const response = await fetch(modelmaticLogoUrl)
		if (!response.ok) return null
		const blob = await response.blob()
		const dataUrl = await new Promise<string | null>((resolve) => {
			const reader = new FileReader()
			reader.onloadend = () => resolve(reader.result as string)
			reader.onerror = () => resolve(null)
			reader.readAsDataURL(blob)
		})
		if (!dataUrl) return null

		const { width, height } = await new Promise<{ width: number; height: number }>(
			(resolve, reject) => {
				const img = new Image()
				img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
				img.onerror = () => reject(new Error('Failed to load image'))
				img.src = dataUrl
			}
		)
		return { dataUrl, width, height }
	} catch {
		return null
	}
}

function fitLogoDimensions(
	natWidth: number,
	natHeight: number,
	maxW: number,
	maxH: number
): { width: number; height: number } {
	const scale = Math.min(maxW / natWidth, maxH / natHeight)
	return {
		width: natWidth * scale,
		height: natHeight * scale,
	}
}

function sanitizeFilename(name: string): string {
	const sanitized = name.replace(/[^a-zA-Z0-9-_ ]/g, '').trim() || 'transcript'
	return sanitized.slice(0, 80)
}

function pad2(n: number): string {
	return n.toString().padStart(2, '0')
}

function formatMillisAsClock(ms: number): string {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000))
	const h = Math.floor(totalSeconds / 3600)
	const m = Math.floor((totalSeconds % 3600) / 60)
	const s = totalSeconds % 60
	if (h > 0) {
		return `${h}:${pad2(m)}:${pad2(s)}`
	}
	return `${pad2(m)}:${pad2(s)}`
}

function formatDurationSeconds(totalSeconds: number): string {
	const s = Math.max(0, Math.floor(totalSeconds))
	const h = Math.floor(s / 3600)
	const m = Math.floor((s % 3600) / 60)
	const sec = s % 60
	if (h > 0) {
		return `${h}:${pad2(m)}:${pad2(sec)}`
	}
	return `${pad2(m)}:${pad2(sec)}`
}

export async function exportTranscriptToPdf(
	transcript: TranscriptResponse,
	metadata?: TranscriptExportMetadata
): Promise<void> {
	const doc = new jsPDF({ unit: 'mm', format: 'a4' })
	const pageHeight = doc.internal.pageSize.getHeight()
	let y = MARGIN + HEADER_HEIGHT

	const bannerData = await loadBannerWithDimensions()
	const logoData = bannerData ? null : await loadLogoWithDimensions()

	const addHeader = () => {
		if (bannerData) {
			const { width: bannerW, height: bannerH } = fitLogoDimensions(
				bannerData.width,
				bannerData.height,
				BANNER_MAX_WIDTH,
				LOGO_MAX_HEIGHT
			)
			const bannerX = (PAGE_WIDTH - bannerW) / 2
			try {
				doc.addImage(bannerData.dataUrl, 'PNG', bannerX, 8, bannerW, bannerH)
			} catch {
				doc.setFontSize(12)
				doc.setFont('helvetica', 'bold')
				doc.setTextColor(100, 100, 100)
				doc.text('ModelMatic', PAGE_WIDTH / 2, 8 + bannerH / 2, { align: 'center' })
				doc.setTextColor(0, 0, 0)
			}
		} else if (logoData) {
			const { width: logoW, height: logoH } = fitLogoDimensions(
				logoData.width,
				logoData.height,
				LOGO_MAX_WIDTH,
				LOGO_MAX_HEIGHT
			)
			const gap = 2
			doc.setFontSize(12)
			doc.setFont('helvetica', 'bold')
			doc.setTextColor(100, 100, 100)
			const textWidth = doc.getTextWidth('ModelMatic')
			const totalWidth = logoW + gap + textWidth
			const startX = (PAGE_WIDTH - totalWidth) / 2
			try {
				doc.addImage(logoData.dataUrl, 'PNG', startX, 8, logoW, logoH)
				doc.text('ModelMatic', startX + logoW + gap, 8 + logoH / 2)
			} catch {
				doc.text('ModelMatic', PAGE_WIDTH / 2, 8 + logoH / 2, { align: 'center' })
			}
			doc.setTextColor(0, 0, 0)
		} else {
			doc.setFontSize(12)
			doc.setFont('helvetica', 'bold')
			doc.setTextColor(100, 100, 100)
			doc.text('ModelMatic', PAGE_WIDTH / 2, 8 + LOGO_MAX_HEIGHT / 2, { align: 'center' })
			doc.setTextColor(0, 0, 0)
		}

		if (metadata?.projectName) {
			doc.setFontSize(HEADER_FONT_SIZE)
			doc.setFont('helvetica', 'normal')
			doc.setTextColor(80, 80, 80)
			const projectLines = doc.splitTextToSize(metadata.projectName, 60)
			doc.text(projectLines, PAGE_WIDTH - MARGIN, 12, { align: 'right' })
			doc.setTextColor(0, 0, 0)
		}
	}

	const addFooter = (pageNum: number) => {
		doc.setFontSize(FOOTER_FONT_SIZE)
		doc.setTextColor(120, 120, 120)
		const footerY = pageHeight - 12

		if (metadata?.companyName) {
			const dateStr = new Date().toISOString().slice(0, 10)
			doc.text(`${dateStr} ${metadata.companyName}`, MARGIN, footerY)
		}

		doc.text(`Page ${pageNum}`, PAGE_WIDTH / 2, footerY, { align: 'center' })

		if (metadata?.createdByDisplay) {
			doc.text(`Created by ${metadata.createdByDisplay}`, PAGE_WIDTH - MARGIN, footerY, {
				align: 'right',
			})
		}
		doc.setTextColor(0, 0, 0)
	}

	const ensureSpace = (needed: number): void => {
		if (y + needed > pageHeight - 30) {
			doc.addPage()
			y = MARGIN + HEADER_HEIGHT
			addHeader()
		}
	}

	const writeTextLines = (lines: string[], fontSize: number, fontStyle: 'normal' | 'bold'): void => {
		doc.setFontSize(fontSize)
		doc.setFont('helvetica', fontStyle)
		const lineHeight = doc.getLineHeight() / doc.internal.scaleFactor
		for (const line of lines) {
			ensureSpace(lineHeight)
			doc.text(line, MARGIN, y)
			y += lineHeight
		}
	}

	addHeader()

	const titleRaw = metadata?.artifactName ?? 'Transcript'
	doc.setFontSize(TITLE_FONT_SIZE)
	doc.setFont('helvetica', 'bold')
	const titleLines = wrapTextForPdf(doc, titleRaw, CONTENT_WIDTH)
	ensureSpace(titleLines.length * TITLE_LINE_HEIGHT + SECTION_SPACING)
	doc.text(titleLines, MARGIN, y)
	y += titleLines.length * TITLE_LINE_HEIGHT + SECTION_SPACING

	const subtitleParts: string[] = []
	const dur = transcript.metadata?.duration_seconds
	if (dur != null && dur > 0) {
		subtitleParts.push(`Duration: ${formatDurationSeconds(dur)}`)
	}
	const lang = transcript.metadata?.language
	if (lang) {
		subtitleParts.push(`Language: ${lang}`)
	}
	if (subtitleParts.length > 0) {
		const subLine = subtitleParts.join(' · ')
		const subLines = wrapTextForPdf(doc, subLine, CONTENT_WIDTH)
		doc.setTextColor(80, 80, 80)
		writeTextLines(subLines, SUBTITLE_FONT_SIZE, 'normal')
		doc.setTextColor(0, 0, 0)
		y += 4
	}

	doc.setFont('helvetica', 'normal')
	doc.setFontSize(BODY_FONT_SIZE)

	const utterances = transcript.utterances
	if (utterances && utterances.length > 0) {
		for (const u of utterances) {
			const speakerLineRaw = `${u.speaker} [${formatMillisAsClock(u.start)}]`
			const speakerLines = wrapTextForPdf(doc, speakerLineRaw, CONTENT_WIDTH)
			const bodyLines = wrapTextForPdf(doc, u.text || '', CONTENT_WIDTH)

			writeTextLines(speakerLines, SPEAKER_LINE_FONT_SIZE, 'bold')
			writeTextLines(bodyLines, BODY_FONT_SIZE, 'normal')
			ensureSpace(UTTERANCE_BLOCK_GAP_MM)
			y += UTTERANCE_BLOCK_GAP_MM
		}
	} else {
		const contentLines = wrapTextForPdf(doc, transcript.content || '', CONTENT_WIDTH)
		writeTextLines(contentLines, BODY_FONT_SIZE, 'normal')
	}

	const totalPages = doc.getNumberOfPages()
	for (let p = 1; p <= totalPages; p++) {
		doc.setPage(p)
		addFooter(p)
	}

	const dateStr = new Date().toISOString().slice(0, 10)
	const baseName = sanitizeFilename(metadata?.artifactName ?? 'transcript')
	doc.save(`transcript-${baseName}-${dateStr}.pdf`)
}
